/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

import Link from "@mui/material/Link";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PageLayout from "examples/LayoutContainers/PageLayout";

import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { decodeInvId } from "utils/invoiceCrypto";

const BUSINESS_NAME = process.env.REACT_APP_BUSINESS_NAME || "YouCloud Technology";

// ── tiny helpers ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  Finalized: { bg: "#d4edda", color: "#155724" },
  Draft:     { bg: "#fff3cd", color: "#856404" },
};

const PAYMENT_STATUS_STYLES = {
  Paid:   { bg: "#d4edda", color: "#155724" },
  Unpaid: { bg: "#f8d7da", color: "#721c24" },
};

function PaymentStatusBadge({ paid }) {
  const label = paid ? "Paid" : "Unpaid";
  const style = PAYMENT_STATUS_STYLES[label];
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </MDBox>
  );
}

function StatusBadge({ value }) {
  const style = STATUS_STYLES[value] ?? { bg: "#e2e3e5", color: "#383d41" };
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </MDBox>
  );
}

function SectionHeader({ label }) {
  return (
    <MDBox mt={2.5} mb={0.5}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color="info"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.72rem" }}
      >
        {label}
      </MDTypography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
    </MDBox>
  );
}

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <MDBox display="flex" alignItems="flex-start" mb={0.75}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color="text"
        sx={{ minWidth: 140, flexShrink: 0, textTransform: "none" }}
      >
        {label}
      </MDTypography>
      <MDTypography
        variant="caption"
        color="text"
        sx={{ textTransform: "none", wordBreak: "break-word" }}
      >
        {value}
      </MDTypography>
    </MDBox>
  );
}

// ── main component ────────────────────────────────────────────────────────────

function ViewInvoice() {
  const { invId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 0 = try merchant logo, 1 = platform logo, 2 = light platform logo
  const [logoFallback, setLogoFallback] = useState(0);

  useEffect(() => {
    const decodedId = decodeInvId(invId);
    if (!decodedId) {
      setError("Invalid invoice link.");
      setLoading(false);
      return;
    }

    const baseUrl = process.env.REACT_APP_BASE_URL;
    axios
      .get(`${baseUrl}/api/merchantPortal/v1/invoices/single/${decodedId}`)
      .then((res) => {
        setInvoice(res.data?.invoice ?? res.data);
        setPayment(res.data?.payment ?? null);
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setError("Invoice not found.");
        } else {
          setError("Failed to load invoice. Please try again later.");
        }
      })
      .finally(() => setLoading(false));
  }, [invId]);

  // ── derived values ──────────────────────────────────────────────────────────

  const items = invoice?.items ?? [];
  // Use API-returned totals directly — avoids floating-point drift
  const grandTotal   = parseFloat(invoice?.totalAmount ?? 0);
  const isTaxEnabled = invoice?.hasTax === true || invoice?.hasTax === 1 || invoice?.hasTax === "1";
  const taxPct       = isTaxEnabled ? parseFloat(invoice.taxPercent ?? 0) : 0;
  const taxAmount    = isTaxEnabled ? parseFloat(invoice.taxAmount ?? 0) : 0;
  const totalWithTax = grandTotal + taxAmount;
  const statusLabel  = invoice?.isDraft ? "Draft" : "Finalized";

  const merchantLogoUrl = invoice?.mid
    ? `${process.env.REACT_APP_BASE_URL}/api/merchantPortal/v1/merchant/${invoice.mid}/logo`
    : null;
  let logoSrc = brandDark;
  if (logoFallback === 0 && merchantLogoUrl) logoSrc = merchantLogoUrl;
  else if (logoFallback >= 2) logoSrc = brandWhite;

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <PageLayout background="light">
      {/* ── top accent bar ── */}
      <MDBox
        sx={{
          width: "100%",
          height: "6px",
          background: "linear-gradient(90deg, #1a73e8 0%, #0d5db4 100%)",
        }}
      />

      <MDBox
        px={{ xs: 2, sm: 4 }}
        py={4}
        minHeight="calc(100vh - 6px)"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {/* ── brand header ── */}
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          mb={3}
        >
          <MDBox
            component="img"
            src={logoSrc}
            alt={BUSINESS_NAME}
            sx={{ height: 48, mb: 1, objectFit: "contain" }}
            onError={() => {
              // Merchant logo missing -> platform logo -> light platform logo
              console.warn(`Failed to load logo from ${logoSrc}`);
              setLogoFallback((prev) => (prev < 2 ? prev + 1 : prev));
            }}
          />
          <MDTypography
            variant="caption"
            color="text"
            sx={{ textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.67rem" }}
          >
            Invoice
          </MDTypography>
        </MDBox>

        {/* ── content card ── */}
        <Card
          sx={{
            width: "100%",
            maxWidth: 820,
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            borderRadius: "12px",
          }}
        >
          {loading && (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress color="info" />
            </MDBox>
          )}

          {!loading && error && (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={8} px={3}>
              <MDTypography variant="body2" color="error" align="center">
                {error}
              </MDTypography>
            </MDBox>
          )}

          {!loading && !error && invoice && (
            <MDBox p={{ xs: 2.5, sm: 4 }}>

              {/* ── invoice header row ── */}
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                mb={2}
              >
                <MDBox>
                  <MDTypography variant="h5" fontWeight="bold" gutterBottom={false}>
                    {invoice.invoiceNumber ?? "—"}
                  </MDTypography>
                  {invoice.issueDate && (
                    <MDTypography variant="caption" color="text" sx={{ textTransform: "none" }}>
                      Issued: {invoice.issueDate}
                    </MDTypography>
                  )}
                </MDBox>
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems={{ xs: "flex-start", sm: "flex-end" }}
                  mt={{ xs: 1, sm: 0 }}
                  gap={0.5}
                >
                  {/* <StatusBadge value={statusLabel} /> */}
                  {invoice.expiryDate && (
                    <MDTypography variant="caption" color="text" sx={{ textTransform: "none" }}>
                      Expires: {invoice.expiryDate}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              <Divider sx={{ mb: 0 }} />

              {/* ── billing & shipping ── */}
              <SectionHeader label="Billing Info" />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoRow label="Billed To:" value={invoice.billingCompany} />
                  <InfoRow label="Billing Address:" value={invoice.billingAddress} />
                  <InfoRow label="Currency:" value={invoice.currency} />
                </Grid>
                {invoice.shippingAddress && (
                  <Grid item xs={12} sm={6}>
                    <InfoRow label="Shipping Address:" value={invoice.shippingAddress} />
                  </Grid>
                )}
              </Grid>

              {/* ── items table ── */}
              <SectionHeader label="Items" />

              {/* header row */}
              <Grid
                container
                spacing={1}
                sx={{
                  mb: 0.5,
                  px: 1,
                  py: 0.75,
                  borderRadius: "6px",
                  backgroundColor: "rgba(0,0,0,0.04)",
                }}
              >
                <Grid item xs={5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">
                    Item Name
                  </MDTypography>
                </Grid>
                <Grid item xs={2.5} sx={{ textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">
                    Rate
                  </MDTypography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">
                    Qty
                  </MDTypography>
                </Grid>
                <Grid item xs={2.5} sx={{ textAlign: "right" }}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">
                    Total
                  </MDTypography>
                </Grid>
              </Grid>

              {/* item rows */}
              {items.map((item, idx) => {
                // Prefer API-calculated total; fall back to local calculation
                const lineTotal = item.total != null
                  ? parseFloat(item.total)
                  : parseFloat(item.rate ?? 0) * parseFloat(item.quantity ?? 1);
                return (
                  <Grid
                    container
                    spacing={1}
                    key={item.id ?? idx}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Grid item xs={5}>
                      <MDTypography variant="caption" sx={{ textTransform: "none" }}>
                        {item.itemName}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={2.5} sx={{ textAlign: "right" }}>
                      <MDTypography variant="caption" sx={{ textTransform: "none" }}>
                        {parseFloat(item.rate ?? 0).toFixed(2)}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "right" }}>
                      <MDTypography variant="caption" sx={{ textTransform: "none" }}>
                        {item.quantity}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={2.5} sx={{ textAlign: "right" }}>
                      <MDTypography variant="caption" fontWeight="medium" sx={{ textTransform: "none" }}>
                        {lineTotal.toFixed(2)}
                      </MDTypography>
                    </Grid>
                  </Grid>
                );
              })}

              {/* totals block */}
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                mt={1.5}
                mb={0.5}
                gap={0.5}
              >
                <MDBox display="flex" gap={3}>
                  <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "none" }}>
                    Sub-Total:
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ textTransform: "none", minWidth: 80, textAlign: "right" }}>
                    {grandTotal.toFixed(2)} {invoice.currency}
                  </MDTypography>
                </MDBox>
                {isTaxEnabled && taxAmount > 0 && (
                  <MDBox display="flex" gap={3}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "none" }}>
                      Tax ({taxPct}%):
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ textTransform: "none", minWidth: 80, textAlign: "right" }}>
                      {taxAmount.toFixed(2)} {invoice.currency}
                    </MDTypography>
                  </MDBox>
                )}
                <MDBox
                  display="flex"
                  gap={3}
                  sx={{
                    borderTop: "2px solid rgba(26,115,232,0.3)",
                    pt: 0.75,
                    mt: 0.25,
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" color="info" sx={{ textTransform: "none" }}>
                    Grand Total:
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color="info"
                    sx={{ textTransform: "none", minWidth: 80, textAlign: "right" }}
                  >
                    {totalWithTax.toFixed(2)} {invoice.currency}
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* ── payment info ── */}
              {(invoice.hasPaymentLink === "1" || invoice.hasPaymentLink === 1 || invoice.hasPaymentLink === true) && (
                <>
                  <SectionHeader label="Payment" />
                  <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={2} mb={1}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "none" }}>
                        Payment Status:
                      </MDTypography>
                      <PaymentStatusBadge paid={invoice.paymentStatus === true || invoice.paymentStatus === 1} />
                    </MDBox>
                    {invoice.paymentLink && invoice.paymentStatus !== true && invoice.paymentStatus !== 1 && (
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: "none" }}>
                          Pay Now:
                        </MDTypography>
                        <Link
                          href={invoice.paymentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontSize: "0.78rem", fontWeight: 600 }}
                        >
                          Click here to pay
                        </Link>
                      </MDBox>
                    )}
                  </MDBox>
                  {/* {invoice.paymentLink && !(invoice.paymentStatus === true || invoice.paymentStatus === 1) && (
                    <InfoRow label="Payment Link Reference  :" value={invoice.paymentLink} />
                  )} */}
                </>
              )}

              {/* ── terms ── */}
              {invoice.conditions && (
                <>
                  <SectionHeader label="Notes" />
                  <MDBox mb={0.5}>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                      sx={{ display: "block", mb: 0.25, textTransform: "none" }}
                    >
                      Terms &amp; Conditions
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{
                        display: "block",
                        textTransform: "none",
                        whiteSpace: "pre-wrap",
                        p: 1.25,
                        borderRadius: "6px",
                        backgroundColor: "rgba(0,0,0,0.03)",
                        border: "1px solid rgba(0,0,0,0.07)",
                      }}
                    >
                      {invoice.conditions}
                    </MDTypography>
                  </MDBox>
                </>
              )}

            </MDBox>
          )}
        </Card>

        {/* ── footer ── */}
        <MDBox mt={3} pb={2} textAlign="center">
          <MDTypography variant="caption" color="text" sx={{ fontSize: "0.68rem" }}>
            &copy; {new Date().getFullYear()} {BUSINESS_NAME}. All rights reserved.
          </MDTypography>
        </MDBox>
      </MDBox>
    </PageLayout>
  );
}

export default ViewInvoice;
